import { Content as PDFContent, TableCell } from 'pdfmake/interfaces'
import {
  AUDIT_RESULT,
  AuditReadinessTestRecord,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { PRIMARY_INFRA_ROLES } from 'src/pages/Services/Services.constants'
import { SERVICE_ROLE } from '@trustero/trustero-api-web/lib/service/service_pb'
import { Framework } from '@trustero/trustero-api-web/lib/model/framework_pb'
import { Content } from '../../context/ContentContext'
import { Overview, Service } from '../../xgenerated'
import { compareNatural } from '../sort'
import { PDFGenerator } from './PDFGenerator'

/** A control paired with its latest control test results from audit readiness. */
export type ControlWithTests = {
  control: Control.AsObject
  tests: AuditReadinessTestRecord[]
}

export class SOC2Report {
  private pdf: PDFGenerator

  constructor() {
    this.pdf = new PDFGenerator()
  }

  async report(
    entity: string,
    content: Content,
    services: Service[],
    frameworks: Framework.AsObject[],
    controls: ControlWithTests[],
  ): Promise<Blob> {
    this.pdf.setFooter((page: number, _: number) => {
      if (page > 2) {
        return {
          columns: [
            {
              text: `©${new Date().getFullYear()} ${entity} | All rights reserved | CONFIDENTIAL`,
              alignment: 'left',
            },
            { text: page.toString(), bold: true, alignment: 'right' },
          ],
          style: ['footer', 'caption'],
        }
      }
      return ''
    })

    await this.pdf.addTitlePage(
      `Pro Forma Soc 2 Report`,
      'A Type 2 Report on Controls Relevant to Security',
      `Prepared by Trustero for ${entity}`,
    )

    this.pdf.addTOC()
    this.pdf.addSection('I', "Independent Service Auditor's Report")
    this.addAuditorSection()
    this.pdf.addSection('II', "Management's Assertion")
    this.addAuditorSection()
    this.pdf.addSection('III', 'Description of the System')
    this.addSystemDescription(entity, content, services)
    this.pdf.addSection(
      'IV',
      'Description of Criteria, Controls, Tests and Results of Tests',
    )
    this.addControlTestMatrices(entity, frameworks, controls)

    return await this.pdf.toBlob()
  }

  private addAuditorSection(): void {
    this.pdf.addBlock({
      text: 'This section is to be filled out by the auditor',
      pageBreak: 'before',
      margin: [20, 20, 20, 30],
      style: 'highlight',
    })
  }

  private getOverview(id: string, content: Content): Overview | undefined {
    return content.overviews.find((overview: Overview) => overview.id === id)
  }

  /* SECTION III */
  private addSystemDescription(
    entity: string,
    content: Content,
    services: Service[],
  ): void {
    this.addOverview(entity, content)
    this.addControlEnvironment(entity, content, services)
  }

  private addOverview(entity: string, content: Content): void {
    this.pdf.addHeading('Overview Of Operations', false, 'before')
    this.pdf.addSubHeading('Company Background')
    const background = this.getOverview('tro0', content) as Overview
    background.document?.material
      ? this.pdf.addMarkdown(background.document.material)
      : this.addOverviewReminder()
    this.pdf.addSubHeading('Description of Services Provided')
    const description = this.getOverview('tro1', content) as Overview
    description.document?.material
      ? this.pdf.addMarkdown(description.document.material)
      : this.addOverviewReminder()
    this.pdf.addSubHeading(
      'Principal Service Commitments And System Requirements',
    )
    const components = this.getOverview('tro2', content) as Overview
    components.document?.material
      ? this.pdf.addMarkdown(components.document.material)
      : this.addOverviewReminder()
  }

  private addOverviewReminder(): void {
    this.pdf.addBlock({
      stack: [`[This information has yet to be provided]`],
      margin: [30, 30, 30, 30],
      style: 'highlight',
      alignment: 'center',
    })
  }

  private addControlEnvironment(
    entity: string,
    content: Content,
    services: Service[],
  ): void {
    this.pdf.addHeading(
      'Relevant Aspects of the Control Environment, Risk Assessment, Information and Communication, and Monitoring',
    )
    this.pdf.addText(
      `[The applicable trust services criteria were used to evaluate the suitability of design and operating effectiveness of controls stated in the description. Although the applicable trust services criteria and related controls are included in Section IV, they are an integral part of Trustero's description of the system. This section provides information about the five interrelated components of internal control at ${entity}, including:`,
      true,
    )
    this.pdf.addList([
      'Control environment',
      'Risk assessment',
      'Control activities',
      'Information and communication',
      'Monitoring controls]',
    ])
    this.addToBeFilledIn(entity)
    this.pdf.addSubHeading('Control Environment')
    this.addToBeFilledIn(entity)
    this.pdf.addSubHeading('Risk Assessment')
    this.addToBeFilledIn(entity)
    this.pdf.addSubHeading('Control Activities')
    this.pdf.addText(
      `[${entity}'s control activities are defined through its established policies and procedures which address individual risks associated with the achievement of the Company’s objectives. Such statements may be documented, explicitly stated in communications, or implied through actions and decisions. Policies serve as the basis for procedures. Control activities are deployed through policies that establish what is expected and procedures that put policies into action.]`,
      true,
    )
    this.addToBeFilledIn(entity)
    this.pdf.addSubHeading('Information and Communication')
    this.addToBeFilledIn(entity)
    this.pdf.addSubHeading('Monitoring Controls')
    this.addToBeFilledIn(entity)
    this.pdf.addSubHeading(
      'Components of the System Used to Provide the Services',
    )
    this.pdf.addText(
      '[The purpose of the system description is to delineate the boundaries of the system, which includes the services and commitments outlined above and the five components described below: infrastructure, software, people, data, and processes and procedures.]',
      true,
    )
    this.addServices(services)

    const cuecs = this.getOverview('tro4', content) as Overview
    this.pdf.addSubHeading('Complementary User Entity Controls')

    if (cuecs.document) {
      const material = cuecs.document.material.replace(
        '# Complementary User Entity Controls',
        '',
      )
      this.pdf.addMarkdown(material)
    } else {
      this.addOverviewReminder()
    }

    const csocs = this.getOverview('tro3', content) as Overview
    this.pdf.addSubHeading('Complementary Subservice Organizations Controls')

    if (csocs.document) {
      const material = csocs.document.material.replace(
        '# Complementary Subservice Organizations Controls',
        '',
      )
      this.pdf.addMarkdown(material)
    } else {
      this.addOverviewReminder()
    }
    this.pdf.addHeading(
      'Procedures for Assessing Completeness and Accuracy of Information Provided by the Entity (IPE)',
    )
    this.addToBeFilledIn(entity)
  }

  private addToBeFilledIn(entity: string): void {
    this.pdf.addBlock({
      text: `The following sections are to be filled by the auditor based on the ${entity}'s controls and policies`,
      margin: [30, 30, 30, 30],
      alignment: 'center',
      style: 'highlight',
    })
  }

  private addServices(services: Service[]): void {
    const product: Record<string, PDFContent>[][] = []
    services.forEach((service: Service) => {
      const isPrimaryInfra = service.service_roles?.some((role: SERVICE_ROLE) =>
        PRIMARY_INFRA_ROLES.includes(role),
      )
      if (isPrimaryInfra && !service.dismissed) {
        product.push([{ text: service.name }, { text: service.content }])
      }
    })

    this.pdf.addTable({
      widths: [250, 250],
      headerRows: 2,
      body: [
        [
          {
            text: 'Primary Infrastructure and Software',
            style: 'tableHeader',
            colSpan: 2,
            alignment: 'center',
          },
          {},
        ],
        [
          {
            text: 'System / Application',
            style: 'tableSubHeader',
            alignment: 'center',
          },
          {
            text: 'Business Function / Description',
            style: 'tableSubHeader',
            alignment: 'center',
          },
        ],
      ].concat(product),
    })

    const tools: Record<string, PDFContent>[][] = []
    services.forEach((service: Service) => {
      const isPrimaryInfra = service.service_roles?.some((role: SERVICE_ROLE) =>
        PRIMARY_INFRA_ROLES.includes(role),
      )
      if (!isPrimaryInfra && !service.dismissed) {
        tools.push([{ text: service.name }, { text: service.content }])
      }
    })
    this.pdf.addTable({
      widths: [250, 250],
      headerRows: 2,
      body: [
        [
          {
            text: 'Supporting Tools',
            style: 'tableHeader',
            colSpan: 2,
            alignment: 'center',
          },
          {},
        ],
        [
          {
            text: 'System / Application',
            style: 'tableSubHeader',
            alignment: 'center',
          },
          {
            text: 'Business Function / Description',
            style: 'tableSubHeader',
            alignment: 'center',
          },
        ],
      ].concat(tools),
    })
  }

  //SECTION 4 Functions
  private addControlTestMatrices(
    entity: string,
    frameworks: Framework.AsObject[],
    controls: ControlWithTests[],
  ): void {
    this.addCriteriaToControlMatrix(
      entity,
      frameworks,
      controls.map(({ control }) => control),
    )
    this.addControlToObservationMatrix(entity, controls)
  }

  private addControlToObservationMatrix(
    entity: string,
    controls: ControlWithTests[],
  ): void {
    this.pdf.addHeading(
      `Security Criterion Mapped to ${entity} Controls and Observation of Controls`,
      false,
      'before',
    )

    const headers: TableCell[][] = [
      [
        { text: 'No', style: 'tableHeader', alignment: 'center' },

        {
          text: 'Description of Controls',
          style: 'tableHeader',
          alignment: 'center',
        },
        { text: 'Criteria', style: 'tableHeader', alignment: 'center' },
        {
          text: 'Test Applied',
          style: 'tableHeader',
          alignment: 'center',
        },
        { text: 'Test Results', style: 'tableHeader', alignment: 'center' },
      ],
    ]

    const results = controls.map(({ control, tests }) => {
      return {
        control: control,
        tests: tests.map((test) => {
          return [{ text: test.getTestName() }, { text: observation(test) }]
        }),
      }
    })

    const table: TableCell[][] = results.flatMap((result) => {
      const control = result.control
      const tests = result.tests

      const test = tests.pop()
      const header: TableCell[] = [
        { text: control.modelId, rowSpan: tests.length + 1 },
        {
          text: control.objective.replace('##', '').trim(),
          rowSpan: tests.length + 1,
        },
        {
          text: frameworks(control.frameworkIdsList).join(', '),
          rowSpan: tests.length + 1,
        },
        test ? test[0] : {},
        test ? test[1] : {},
      ]

      const rest: TableCell[][] = tests.map((test) => {
        return ['', '', '', test[0], test[1]]
      })

      return [header].concat(rest)
    })

    this.pdf.addTable({
      widths: [30, 175, 40, 175, 70],
      headerRows: 1,
      body: headers.concat(table),
      dontBreakRows: true,
    })
  }

  private addCriteriaToControlMatrix(
    entity: string,
    frameworks: Framework.AsObject[],
    controls: Control.AsObject[],
  ): void {
    this.pdf.addHeading(
      `${entity} Controls Mapped to the Security Criterion`,
      false,
      'before',
    )

    const rows = frameworks.filter((framework) => {
      if (framework.group !== framework.modelId) {
        return (
          controlIdsInFramework(
            controls.filter((control) => !control.isDismissed),
            framework,
          ).length > 0
        )
      }
      return frameworks.some((cc) => {
        return (
          cc.group === framework.modelId &&
          controlIdsInFramework(controls, cc).length > 0
        )
      })
    })
    const body = inFrameworkOrder(rows).map((framework) => {
      if (framework.group === framework.modelId) {
        return [
          {
            text: framework.name,
            style: 'tableSubHeader',
            colSpan: 3,
          },
          {},
          {},
        ]
      } else {
        return [
          { text: framework.name },
          {
            text: controlIdsInFramework(controls, framework).join(', '),
          },
          { text: framework.description },
        ]
      }
    })

    const headers: TableCell[][] = [
      [
        { text: 'Criteria', style: 'tableHeader', alignment: 'center' },
        {
          text: 'Supporting Control',
          style: 'tableHeader',
          alignment: 'center',
        },
        {
          text: 'Criteria Description',
          style: 'tableHeader',
          alignment: 'center',
        },
      ],
    ]

    this.pdf.addTable({
      widths: [100, 100, 300],
      headerRows: 1,
      body: headers.concat(body),
      dontBreakRows: true,
    })
  }
}

// Orders mixed section-header and requirement rows the way the framework reads: by
// framework, then section, with each section header followed by its requirements in
// display order. A section's position is its header's display_order, or its lowest
// requirement display_order when the account has no header row for it (built-in
// frameworks number headers and requirements as one sequence).
function inFrameworkOrder(rows: Framework.AsObject[]): Framework.AsObject[] {
  const isSection = (f: Framework.AsObject) => f.group === f.modelId
  const sectionOrder = new Map<string, number>()
  rows.forEach((f) => {
    if (isSection(f)) return
    const current = sectionOrder.get(f.group)
    if (current === undefined || f.displayOrder < current) {
      sectionOrder.set(f.group, f.displayOrder)
    }
  })
  rows
    .filter(isSection)
    .forEach((s) => sectionOrder.set(s.modelId, s.displayOrder))
  const sectionKey = (f: Framework.AsObject) =>
    sectionOrder.get(f.group) ?? Number.MAX_SAFE_INTEGER
  return [...rows].sort(
    (a, b) =>
      compareNatural(a.complianceFrameworkId, b.complianceFrameworkId) ||
      sectionKey(a) - sectionKey(b) ||
      compareNatural(a.group, b.group) ||
      Number(isSection(b)) - Number(isSection(a)) ||
      a.displayOrder - b.displayOrder ||
      compareNatural(a.name, b.name),
  )
}

function cc(framework: string): string {
  return framework
    .match(/[A-Z]+\d.\d+/g)
    ?.values()
    .next().value as string
}

function frameworks(frameworkIds: string[]): string[] {
  return frameworkIds
    .filter((cc) => cc.startsWith('trf-soc2-'))
    .map((id) => cc(id))
}

// The observation column reports the latest control test outcome from audit
// readiness -- the same results the controls index shows. A passing control has
// always rendered as an empty cell, so only exceptions and non-results are
// spelled out.
function observation(test: AuditReadinessTestRecord): string {
  switch (test.getResult()) {
    case AUDIT_RESULT.PASS:
      return ''
    case AUDIT_RESULT.FAIL:
      return 'Exceptions Found'
    case AUDIT_RESULT.NOT_APPLICABLE:
      return 'Not Applicable'
    default:
      // NOT_RUN, UNKNOWN and the error results: no outcome to report. Saying
      // nothing ran is honest; calling it an exception would not be.
      return 'Not Tested'
  }
}

function controlIdsInFramework(
  controls: Control.AsObject[],
  framework: Framework.AsObject,
): string[] {
  return controls
    .filter((control) => control.frameworkIdsList.includes(framework.modelId))
    .map((control) => `${control.modelId} ${control.name}`)
}
